/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 23-09-2026
 *
 * Het inlogscherm van create (golf 4e), de ng21-opvolger van `app/states/login/`. Eén component met
 * zeven standen, precies de vijf stappen die de AngularJS-versie kende:
 *
 *   credentials      gebruikersnaam en wachtwoord
 *   domein           de domeinkeuze die het backend teruggaf
 *   project          de projectkeuze die het backend teruggaf
 *   advanced         domein, project, gebruikersnaam en wachtwoord in één formulier
 *   fixed_advanced   hetzelfde, met domein en project vast (na een aanmelding met een projectcode)
 *   code             aanmelden met een projectcode en een e-mailadres
 *   bevestigaccount  de bevestigingscode uit de mail, met een wachtwoord en een naam
 *
 * Dit scherm en niet het `dl-login` uit `dynalearn-common`: dat is één stap met domein, gebruikersnaam
 * en wachtwoord tegen een sessie die `true`/`false` teruggeeft. Create's stappen komen uit het antwoord
 * van het backend (`kiesdomein`/`kiesproject`) en het kent een projectcode en een accountbevestiging;
 * dat in common zetten zou het sessiecontract van admin en material moeten oprekken.
 */
import {ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {TranslatePipe} from '@ngx-translate/core';
import {BsModalService} from 'ngx-bootstrap/modal';
import {TaalService} from 'dynalearn-common/services/taal.service';
import {SessieService} from '../../../services/sessie.service';
import {ToastService} from '../../../services/toast.service';
import {VERSIE} from '../../../app/versie';
import {DlWachtwoordVergetenComponent} from '../dl-wachtwoord-vergeten/dl-wachtwoord-vergeten.component';

/**
 * De standen van het formulier; zie de kop van dit bestand.
 */
type Loginstap = 'credentials' | 'domein' | 'project' | 'advanced' | 'fixed_advanced' | 'code' | 'bevestigaccount';

@Component({
    selector: 'dl-login',
    templateUrl: './dl-login.component.html',
    styleUrls: ['./dl-login.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, RouterLink, TranslatePipe]
})
export class DlLoginComponent implements OnInit {

    /** De sessie: alle negen calls die dit scherm doet, staan daar. */
    private readonly sessie = inject(SessieService);

    /** De taal waarin het backend zijn mails stelt. */
    private readonly taal = inject(TaalService);

    /** Voor de dialoog "wachtwoord vergeten". */
    private readonly modalService = inject(BsModalService);

    /** De melding na een geslaagde aanmelding met een code. */
    private readonly toast = inject(ToastService);

    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly opruimen = inject(DestroyRef);

    /** Het versienummer onderaan het scherm. */
    protected readonly versie = VERSIE;

    /** In welke stap het formulier staat. */
    protected readonly stap = signal<Loginstap>('credentials');

    /** De vertaalsleutel van de melding onder het formulier, of leeg. */
    protected readonly feedback = signal('');

    /**
     * Of er een aanroep naar het backend loopt. Zolang dat zo is, zijn de knoppen dicht en doet een
     * tweede klik niets. Het eind is gegarandeerd: de aanroepen in `ApiService` hebben een eigen
     * wachttijd (`AANMELD_WACHTTIJD`) en geven daarna een fout in plaats van eeuwig te wachten.
     */
    protected readonly bezig = signal(false);

    /** De domeinen waaruit gekozen moet worden, zoals het backend ze teruggaf. */
    protected readonly kiesdomein = signal<string[]>([]);

    /** De projecten waaruit gekozen moet worden, zoals het backend ze teruggaf. */
    protected readonly kiesproject = signal<string[]>([]);

    /**
     * De ingevulde gegevens. Een gewoon object en geen signals: `ngModel` schrijft erin en de template
     * leest het in dezelfde slag.
     */
    protected readonly formulier = {
        username: '',
        password: '',
        password2: '',
        domein: '',
        project: '',
        code: '',
        bevestigingscode: '',
        naam: ''
    };

    /** Of de uitgebreide stap zijn domein- en projectvelden toont. */
    protected readonly metDomeinveld = computed(() => this.stap() === 'advanced');

    /** Of er een wachtwoordstap loopt waarin de twee wachtwoorden gelijk moeten zijn. */
    protected readonly wachtwoordenGelijk = computed(() => this.stap() !== 'bevestigaccount'
        || this.formulier.password === this.formulier.password2);

    /**
     * Volgt de projectcode in de url. Het abonnement en niet de snapshot: bij een tweede code-link in
     * dezelfde tab hergebruikt de router dit component en draait `ngOnInit` niet opnieuw.
     *
     * Dat er niemand is ingelogd, heeft de `actieGuard` al vastgesteld; de code-route heeft die guard
     * niet, want een projectcode betekent juist uitloggen.
     */
    ngOnInit(): void {
        this.route.paramMap.pipe(takeUntilDestroyed(this.opruimen)).subscribe(params => {
            const projectcode = params.get('projectcode');
            if (!projectcode) {
                return;
            }
            //een directe link naar een projectcode betekent altijd eerst uitloggen
            this.sessie.logout();
            this.formulier.code = projectcode;
            this.feedback.set('');
            this.stap.set('code');
        });
    }

    /**
     * Naar de uitgebreide stap, met wat we van de vorige keer weten.
     */
    protected naarAdvanced(): void {
        this.feedback.set('');
        if (this.stap() === 'credentials') {
            this.formulier.domein = this.formulier.domein || localStorage.getItem('logindomein') || '';
            this.formulier.project = this.formulier.project || localStorage.getItem('loginproject') || '';
        }
        this.stap.set('advanced');
    }

    /**
     * Terug naar het gewone inlogformulier; alles blijft staan.
     */
    protected naarNormaal(): void {
        this.feedback.set('');
        this.stap.set('credentials');
    }

    /**
     * Terug naar de eerste stap, met een leeg domein en project.
     */
    protected naarBegin(): void {
        this.feedback.set('');
        this.formulier.domein = '';
        this.formulier.project = '';
        this.stap.set('credentials');
    }

    /**
     * Naar de aanmelding met een projectcode.
     */
    protected naarCode(): void {
        this.feedback.set('');
        this.stap.set('code');
    }

    /**
     * Logt in. Het antwoord is een volledige login, of de keuze die nog gemaakt moet worden. Een klik
     * terwijl er al een aanroep loopt, doet niets.
     */
    protected async login(): Promise<void> {
        await this.metBlokkade(() => this.loginUitvoeren());
    }

    /**
     * Meldt aan met een projectcode. De eerste keer vraagt dat een bevestigingscode aan; met die code
     * erbij maakt het backend een account aan en logt het meteen in. Een klik terwijl er al een aanroep
     * loopt, doet niets.
     */
    protected async aanmeldenCode(): Promise<void> {
        await this.metBlokkade(() => this.aanmeldenCodeUitvoeren());
    }

    /**
     * Opent de dialoog waarmee een wachtwoordlink aangevraagd wordt.
     */
    protected wachtwoordVergeten(): void {
        this.modalService.show(DlWachtwoordVergetenComponent, {backdrop: 'static'});
    }

    /**
     * Voert de aanroep uit met de knoppen dicht en doet niets als er al een loopt. De knoppen gaan weer
     * open wat er ook gebeurt, ook als de aanroep zelf een fout geeft.
     */
    private async metBlokkade(werk: () => Promise<void>): Promise<void> {
        if (this.bezig()) {
            return;
        }
        this.bezig.set(true);
        try {
            await werk();
        } finally {
            this.bezig.set(false);
        }
    }

    /**
     * De login zelf, zonder blokkade.
     */
    private async loginUitvoeren(): Promise<void> {
        this.feedback.set('LOGIN.BEZIG');
        const stap = this.stap();
        const res = await this.sessie.login({
            username: this.formulier.username,
            password: this.formulier.password,
            ...(['domein', 'project', 'advanced', 'fixed_advanced'].includes(stap)
                ? {domein: this.formulier.domein} : {}),
            ...(['project', 'advanced', 'fixed_advanced'].includes(stap)
                ? {project: this.formulier.project} : {})
        });
        this.feedback.set('');
        if (!res) {
            this.feedback.set('LOGIN.MISLUKT');
            return;
        }
        if (res.loggedin) {
            localStorage.setItem('logindomein', res.domein ?? '');
            localStorage.setItem('loginproject', res.project ?? '');
            await this.naarBouwscherm();
            return;
        }
        if (res.kiesdomein) {
            this.formulier.domein = '';
            this.kiesdomein.set(res.kiesdomein);
            this.stap.set('domein');
            return;
        }
        if (res.domein && res.kiesproject) {
            this.formulier.domein = res.domein;
            this.formulier.project = '';
            this.kiesproject.set(res.kiesproject);
            this.stap.set('project');
            return;
        }
        this.stap.set('credentials');
        this.feedback.set('LOGIN.MISLUKT');
    }

    /**
     * De aanmelding met een projectcode zelf, zonder blokkade.
     */
    private async aanmeldenCodeUitvoeren(): Promise<void> {
        this.feedback.set('LOGIN.CODEBEZIG');
        if (this.stap() === 'code') {
            const res = await this.sessie.aanmeldenCode(this.formulier.code, this.formulier.username,
                this.taal.taal());
            this.feedback.set('');
            switch (res.status) {
                case 'AL_ACCOUNT':
                case 'TOEGANG_MET_ACCOUNT':
                    this.feedback.set(`LOGIN.${res.status}`);
                    this.formulier.domein = res.domein ?? '';
                    this.formulier.project = res.project ?? '';
                    this.stap.set('fixed_advanced'); //advanced, met domein en project verborgen
                    return;
                case 'BEVESTIGINGSCODE_GESTUURD':
                    this.formulier.bevestigingscode = '';
                    this.formulier.password = '';
                    this.formulier.password2 = '';
                    this.stap.set('bevestigaccount');
                    return;
                case 'ONGELDIG':
                    this.feedback.set('LOGIN.CODE_ONBEKEND');
                    return;
                case 'FOUT':
                    this.feedback.set('LOGIN.FOUT_OPNIEUW');
                    return;
                default:
                    this.feedback.set('LOGIN.MISLUKT');
                    return;
            }
        }
        const res = await this.sessie.aanmeldenCode(this.formulier.code, this.formulier.username,
            this.taal.taal(), this.formulier.bevestigingscode, this.formulier.password, this.formulier.naam);
        this.feedback.set('');
        switch (res.status) {
            case 'ingelogd':
                break;
            case 'ONGELDIG':
                this.feedback.set('LOGIN.CODE_ONGELDIG');
                return;
            case 'FOUT':
                this.feedback.set('LOGIN.FOUT_OPNIEUW');
                return;
            default:
                this.feedback.set('LOGIN.MISLUKT');
                return;
        }
        localStorage.setItem('logindomein', res.domein ?? '');
        localStorage.setItem('loginproject', res.project ?? '');
        await this.naarBouwscherm();
        this.toast.toon('LOGIN.AANGEMELD_MET_CODE');
    }

    /**
     * Door naar het bouwscherm, met de actie uit de url mee als die er was.
     */
    private async naarBouwscherm(): Promise<void> {
        const params = this.route.snapshot.paramMap;
        const query = this.route.snapshot.queryParamMap;
        const actie = params.get('openAction') ?? query.get('actie');
        const arg = params.get('openArg') ?? query.get('arg');
        await this.router.navigate(['/build'], {queryParams: actie ? {actie, arg} : {}});
    }
}
